import { createAsyncThunk } from "@reduxjs/toolkit";

import { services } from "../../services";
import {
	LitheStepEnum,
	type TLitheCreationData,
} from "../../types/lithe.type";
import {
	normalizeLitheStep,
	normalizeLitheSteps,
} from "../../utils/litheUtils";

const buildThunkErrorPayload = (
	error: any,
	fallbackMessage: string
) => {
	const responseData = error?.response?.data;
	const errorMessage =
		responseData?.msg ??
		responseData?.error_msg ??
		error?.message ??
		fallbackMessage;

	return {
		...(responseData ?? {}),
		msg: `Error occurred: ${errorMessage}`,
	};
};

export const createLitheThunk = createAsyncThunk(
	"lithe/createLitheThunk",
	async (
		litheCreationData: TLitheCreationData,
		{ dispatch, rejectWithValue }
	) => {
		try {
			const data = await services.litheService.createLithe(
				litheCreationData
			);
			return data;
		} catch (error: any) {
			console.error("redux error", error);
			return rejectWithValue(
				buildThunkErrorPayload(
					error,
					"Unable to create lithe."
				)
			);
		}
	}
);

export const fetchLitheThunk = createAsyncThunk(
	"lithe/fetchLitheThunk",
	async (litheId: string, { rejectWithValue }) => {
		try {
			const metaData =
				await services.litheService.getLithe(litheId);

			const stepsData = await services.litheService
				.getLitheSteps(litheId)
				.catch(() => null);
			const rawAllSteps = (
				Array.isArray(stepsData?.all_steps) &&
				stepsData.all_steps.length > 0
					? stepsData.all_steps
					: metaData?.all_steps
			) as Array<string> | null | undefined;
			const rawCurrentStep = (
				stepsData?.current_step ??
				metaData?.current_step
			) as string | null | undefined;
			const fallbackCurrentStep =
				metaData?.current_step as
					| string
					| null
					| undefined;

			return {
				...metaData,
				lithe_type:
					stepsData?.lithe_type ?? metaData?.lithe_type,
				all_steps: normalizeLitheSteps(rawAllSteps),
				current_step:
					normalizeLitheStep(rawCurrentStep) ??
					normalizeLitheStep(fallbackCurrentStep) ??
					LitheStepEnum.SPECS,
			};
		} catch (error: any) {
			console.error("redux error", error);
			return rejectWithValue(
				buildThunkErrorPayload(
					error,
					"Unable to load lithe metadata."
				)
			);
		}
	}
);

export const fetchLitheSpecsQuestionsThunk =
	createAsyncThunk(
		"lithe/fetchLitheSpecsQuestionsThunk",
		async (litheId: string, { rejectWithValue }) => {
			try {
				const data =
					await services.litheService.getLitheSpecsQuestions(
						litheId
					);
				return data;
			} catch (error: any) {
				console.error("redux error", error);
				return rejectWithValue(
					buildThunkErrorPayload(
						error,
						"Unable to load the required fields."
					)
				);
			}
		}
	);

export const fetchLitheDataVizQuestionsThunk =
	createAsyncThunk(
		"lithe/fetchLitheDataVizQuestionsThunk",
		async (litheId: string, { rejectWithValue }) => {
			try {
				const data =
					await services.litheService.getLitheDataVizQuestions(
						litheId
					);
				return data;
			} catch (error: any) {
				console.error("redux error", error);
				return rejectWithValue(
					buildThunkErrorPayload(
						error,
						"Unable to load proxy data questions."
					)
				);
			}
		}
	);
// ---------------------------------------------------------------
// Not Required
//
// export const fetchLitheDataVizResultsThunk =
// 	createAsyncThunk(
// 		"lithe/fetchLitheDataVizResultsThunk",
// 		async (litheId: string, { rejectWithValue }) => {
// 			try {
// 				const data =
// 					await services.litheService.getLitheDataVizResults(
// 						litheId
// 					);
// 				return data;
// 			} catch (error: any) {
// 				console.error("redux error", error);
// 				return rejectWithValue({
// 					...error.response.data,
// 					msg: `Error occurred: ${error.response.data.msg}`,
// 				});
// 			}
// 		}
// 	);

export const fetchLitheCashflowVizQuestionsThunk =
	createAsyncThunk(
		"lithe/fetchLitheCashflowVizQuestionsThunk",
		async (litheId: string, { rejectWithValue }) => {
			try {
				const data =
					await services.litheService.getLitheCashflowVizQuestions(
						litheId
					);
				return data;
			} catch (error: any) {
				console.error("redux error", error);
				return rejectWithValue(
					buildThunkErrorPayload(
						error,
						"Unable to load risk factor questions."
					)
				);
			}
		}
	);

// ---------------------------------------------------------------
// Not Required
//
// export const fetchLitheCashflowVizResultsThunk =
// 	createAsyncThunk(
// 		"lithe/fetchLitheCashflowVizResultsThunk",
// 		async (litheId: string, { rejectWithValue }) => {
// 			try {
// 				const data =
// 					await services.litheService.getLitheDataVizResults(
// 						litheId
// 					);
// 				return data;
// 			} catch (error: any) {
// 				console.error("redux error", error);
// 				return rejectWithValue({
// 					...error.response.data,
// 					msg: `Error occurred: ${error.response.data.msg}`,
// 				});
// 			}
// 		}
// 	);

export const fetchLitheProbsQuestionsThunk =
	createAsyncThunk(
		"lithe/fetchLitheProbsQuestionsThunk",
		async (litheId: string, { rejectWithValue }) => {
			try {
				const data =
					await services.litheService.getLitheProbsQuestions(
						litheId
					);
				return data;
			} catch (error: any) {
				console.error("redux error", error);
				return rejectWithValue({
					...error.response.data,
					msg: `Error occurred: ${error.response.data.msg}`,
				});
			}
		}
	);

export const fetchLitheProbsResultsThunk = createAsyncThunk(
	"lithe/fetchLitheProbsResultsThunk",
	async (litheId: string, { rejectWithValue }) => {
		try {
			const data =
				await services.litheService.getLitheProbsResults(
					litheId
				);
			return data;
		} catch (error: any) {
			console.error("redux error", error);
			return rejectWithValue(
				buildThunkErrorPayload(
					error,
					"Unable to load probability results."
				)
			);
		}
	}
);

export const fetchLitheApprovalInfo = createAsyncThunk(
	"lithe/fetchLitheApprovalInfo",
	async (
		{
			litheId,
			isUserGuided = false,
		}: { litheId: string; isUserGuided?: boolean },
		{ rejectWithValue }
	) => {
		try {
			const data =
				await services.litheService.getLitheApprovalInfo(
					litheId,
					isUserGuided
				);
			return data;
		} catch (error: any) {
			console.error("redux error", error);
			return rejectWithValue(
				buildThunkErrorPayload(
					error,
					"Unable to load approval info."
				)
			);
		}
	}
);

export const fetchLitheRFSpecsQuestionsThunk =
	createAsyncThunk(
		"lithe/fetchLitheRFSpecsQuestionsThunk",
		async (
			arg:
				| string
				| {
						litheId: string;
						useManualFlow?: boolean;
				  },
			{ rejectWithValue }
		) => {
			try {
				const litheId =
					typeof arg === "string" ? arg : arg.litheId;
				const useManualFlow =
					typeof arg === "string"
						? false
						: Boolean(arg.useManualFlow);
				const data =
					useManualFlow
						? await services.litheService.getLitheManualRFQuestions(
								litheId
							)
						: await services.litheService.getLitheRFSpecsQuestions(
								litheId
							);
				return data;
			} catch (error: any) {
				console.error("redux error", error);
				return rejectWithValue(
					buildThunkErrorPayload(
						error,
						"Unable to load risk factor specs."
					)
				);
			}
		}
	);

export const fetchLitheManualRFQuestionsThunk =
	createAsyncThunk(
		"lithe/fetchLitheManualRFQuestionsThunk",
		async (litheId: string, { rejectWithValue }) => {
			try {
				const data =
					await services.litheService.getLitheManualRFQuestions(
						litheId
					);
				return data;
			} catch (error: any) {
				console.error("redux error", error);
				return rejectWithValue(
					buildThunkErrorPayload(
						error,
						"Unable to load risk factor questions."
					)
				);
			}
		}
	);

export const fetchLitheRawProbData = createAsyncThunk(
	"lithe/fetchLitheRawProbData",
	async (litheId: string, { rejectWithValue }) => {
		try {
			const data =
				await services.litheService.getLitheRawProbData(
					litheId
				);

			return data;
		} catch (error: any) {
			console.error("redux error", error);
			return rejectWithValue(
				buildThunkErrorPayload(
					error,
					"Unable to load raw probabilities."
				)
			);
		}
	}
);
